import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Chip,
  Pagination,
  Box,
  Typography,
  Button,
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import {
  useApproveNonMatchingAttributes,
  useFetchFailureAnalysis,
  useGetNonMatchingAttributes,
  useDeleteOuterHtml,
} from "../useQuery";
import { useNotification } from "../hooks";
import { DeleteIcon } from "../components-new/icons";
import { useQueryClient } from "@tanstack/react-query";
import { ReportProblem } from "@mui/icons-material";

const StyledTableCell = styled(TableCell)({
  color: "white",
  fontWeight: "600",
  padding: "16px",
  fontSize: "0.875rem",
});

const StyledTableRow = styled(TableRow)({
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: "80vh",
  overflow: "auto",
};

const FailureAnalysisPage = () => {
  const notify = useNotification();
  const location = useLocation();
  const queryClient = useQueryClient();

  const searchParams = new URLSearchParams(location.search);
  const tsrid = searchParams.get("tsrid");

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [selectedElementHTML, setSelectedElementHTML] = useState(null);
  const rowsPerPage = 10;

  const { data, isLoading, isError, error } = useFetchFailureAnalysis(tsrid);

  const TSRid = data?.tableData?.[0]?.idtest_step_result;
  const teststepid = data?.tableData?.[0]?.Test_Step;

  const {
    data: nonMatchingData,
    isLoading: isNonMatchingLoading,
    isError: isNonMatchingError,
    error: nonMatchingError,
  } = useGetNonMatchingAttributes(teststepid, TSRid, {
    enabled: !!teststepid && !!TSRid,
  });

  const ptcrid = nonMatchingData?.ptcrid;
  const foundElements = nonMatchingData?.foundelements || [];
  useEffect(() => {
    if (
      nonMatchingData &&
      nonMatchingData.foundelements &&
      nonMatchingData.foundelements.length > 0
    ) {
      // Set the first element as default
      const firstElement = nonMatchingData.foundelements[0];
      const firstElementId = Object.keys(firstElement)[0];
      setSelectedElementId(firstElementId);
      setSelectedElementHTML(firstElement[firstElementId]);
    } else if (nonMatchingData && nonMatchingData.foundOuterHTML) {
      // Use foundOuterHTML if no elements array
      setSelectedElementHTML(nonMatchingData.foundOuterHTML);
    }
  }, [nonMatchingData]);

  const { mutate: approveAttributes, isLoading: isApproveLoading } =
    useApproveNonMatchingAttributes({
      onSuccess: (data) => {
        notify(data.message || "Attributes approved successfully", "success");
      },
      onError: (error) => {
        notify(error.message || "Failed to approve attributes", "error");
      },
    });

  const { mutate: deleteOuterHtml } = useDeleteOuterHtml({
    onSuccess: (data) => {
      notify(data.message || "Successfully deleted outer HTML", "success");
    },
    onError: (error) => {
      notify(error.message || "Failed to delete outer HTML", "error");
    },
  });

  const handleDeleteOuterHtml = (testCaseResultId, testStepResultId) => {
    deleteOuterHtml({ testCaseResultId, testStepResultId });
    queryClient.invalidateQueries(["failureAnalysis", tsrid]);
    queryClient.invalidateQueries(["nonMatchingAttributes", teststepid]);
  };

  const handleApproveClick = () => {
    if (selectedElementId) {
      approveAttributes({ teststepId: teststepid, elementId: selectedElementId });
    } else {
      notify("No element selected for approval", "error");
    }
  };

  const handleElementSelect = (elementId, elementHTML) => {
    setSelectedElementId(elementId);
    setSelectedElementHTML(elementHTML);
    setModalOpen(false);
  };

  const getStatusChipColor = (status) => {
    return status.toUpperCase() === "PASS" ? "success" : "error";
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error: {error.message}</div>;

  const tableData = data?.tableData || [];
  const paginatedData = tableData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <div className="bg-gray-100 min-h-screen px-4 md:px-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800 text-white py-3 px-4 rounded-md shadow-md mb-4">
          <div className="flex flex-row items-center justify-between">
            <h1 className="text-lg font-semibold mb-2">Failure Analysis</h1>
            <Button
              variant="contained"
              color="primary"
              sx={{
                textTransform: "none",
                height: "45px",
                bgcolor: "#2550D1",
              }}
              startIcon={<ReportProblem />}
              onClick={() => {}}
            >
              Log Defect
            </Button>
          </div>
          <div className="flex flex-wrap">
            <div className="w-1/2 text-left mb-1">
              <span className="text-gray-300 text-sm">Step Number:</span>{" "}
              <span className="font-medium">{data.details?.step_number}</span>
            </div>
            <div className="w-1/2 text-left mb-1">
              <span className="text-gray-300 text-sm">Test Case Id:</span>{" "}
              <span className="font-medium">{data.details?.test_case_id}</span>
            </div>
            <div className="w-1/2 text-left mb-1">
              <span className="text-gray-300 text-sm">Test Case Name:</span>{" "}
              <span className="font-medium">{data.details?.test_case_name}</span>
            </div>
            <div className="w-1/2 text-left mb-1">
              <span className="text-gray-300 text-sm">Step Id:</span>{" "}
              <span className="font-medium">{data.details?.test_step_id}</span>
            </div>
          </div>
        </div>

        {/* Assessment and Action Section */}
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          <div className="bg-white p-6 md:p-8 rounded-md shadow-md md:w-1/2 border border-gray-100">
            <h2 className="text-lg font-semibold text-center mb-5 text-gray-800 pb-2 border-b border-gray-100">
              Our Assessment
            </h2>
            {data?.ourAssessment && Object.keys(data.ourAssessment).length > 0 ? (
              <div>
                <p className="text-gray-700 mb-2">
                  <strong>Issue Type:</strong> {data.ourAssessment.issueType}
                </p>
                <p className="text-gray-700">
                  <strong>Issues:</strong> {data.ourAssessment.issues}
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Recommended Action:</strong>
                  {data.recommended_action}
                </p>
              </div>
            ) : (
              <p className="text-gray-500 italic">No assessment data available at the moment.</p>
            )}
          </div>

          <div className="bg-white p-6 md:p-8 rounded-md shadow-md md:w-1/2 border border-gray-100">
            <h2 className="text-lg font-semibold text-center mb-4 text-gray-800 pb-2 border-b border-gray-100">
              Recommended Action
            </h2>
            {nonMatchingData ? (
              <div>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Non-matching Attributes:</strong>{" "}
                  {nonMatchingData.nonmatchingattr || "None"}
                </Typography>

                <div className="grid grid-cols-1 gap-2 mt-2">
                  <div>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Recorded HTML:</strong>
                    </Typography>
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor: "#f5f5f5",
                        padding: "8px",
                        borderRadius: "4px",
                        overflowX: "auto",
                        border: "1px solid #e0e0e0",
                        fontSize: "0.75rem",
                        maxHeight: "60px",
                        overflowY: "auto",
                      }}
                    >
                      {nonMatchingData.recordedOuterHTML || "No data available"}
                    </Box>
                  </div>

                  <div>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      className="flex justify-between items-center"
                    >
                      <strong>Found HTML:</strong>
                      {foundElements.length > 1 && (
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => setModalOpen(true)}
                          sx={{ textTransform: "none", fontSize: "0.7rem" }}
                        >
                          View All ({foundElements.length})
                        </Button>
                      )}
                    </Typography>
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor: "#f5f5f5",
                        padding: "8px",
                        borderRadius: "4px",
                        overflowX: "auto",
                        border: "1px solid #e0e0e0",
                        fontSize: "0.75rem",
                        maxHeight: "60px",
                        overflowY: "auto",
                      }}
                    >
                      {selectedElementHTML || nonMatchingData.foundOuterHTML || "No data available"}
                    </Box>
                  </div>

                  <div>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Last Passed HTML:</strong>
                    </Typography>
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        backgroundColor: "#f5f5f5",
                        padding: "8px",
                        borderRadius: "4px",
                        overflowX: "auto",
                        border: "1px solid #e0e0e0",
                        fontSize: "0.75rem",
                        maxHeight: "60px",
                        overflowY: "auto",
                      }}
                    >
                      {nonMatchingData.lastPassedOuterHTML || "No data available"}
                    </Box>
                  </div>
                </div>

                <Box mt={2} display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleApproveClick}
                    disabled={isApproveLoading || !selectedElementId}
                  >
                    Approve
                  </Button>
                </Box>
              </div>
            ) : (
              <Typography color="text.secondary" variant="body2" className="italic">
                No attribute data available at the moment.
              </Typography>
            )}
          </div>
        </div>

        {/* Found Elements Modal */}
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          aria-labelledby="found-elements-modal"
        >
          <Box sx={ModalStyle}>
            <Typography id="found-elements-modal" variant="h6" component="h2" mb={2}>
              All Found Elements
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedElementId || ""}
                onChange={(e) => {
                  const elementId = e.target.value;
                  const element = foundElements.find((el) => Object.keys(el)[0] === elementId);
                  if (element) {
                    handleElementSelect(elementId, element[elementId]);
                  }
                }}
              >
                {foundElements.map((element, index) => {
                  const elementId = Object.keys(element)[0];
                  const html = element[elementId];

                  return (
                    <Box key={elementId} mb={2} p={2} border="1px solid #e0e0e0" borderRadius={1}>
                      <FormControlLabel
                        value={elementId}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Element {index + 1} (ID: {elementId})
                            </Typography>
                            <Box
                              sx={{
                                fontFamily: "monospace",
                                backgroundColor: "#f5f5f5",
                                padding: "8px",
                                borderRadius: "4px",
                                overflowX: "auto",
                                fontSize: "0.75rem",
                                maxHeight: "80px",
                                overflowY: "auto",
                              }}
                            >
                              {html}
                            </Box>
                          </Box>
                        }
                      />
                    </Box>
                  );
                })}
              </RadioGroup>
            </FormControl>

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setModalOpen(false)}
                sx={{ ml: 2 }}
              >
                Select
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Results Table */}
        <TableContainer
          component={Paper}
          className="shadow-md rounded-md overflow-hidden border border-gray-200"
        >
          <Table aria-label="failure analysis results">
            <TableHead className="bg-gray-800">
              <TableRow>
                <StyledTableCell>Result ID</StyledTableCell>
                <StyledTableCell>Executed Date</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Screenshot</StyledTableCell>
                <StyledTableCell>Page Source</StyledTableCell>
                <StyledTableCell>Analysis Log</StyledTableCell>
                <StyledTableCell>Log File</StyledTableCell>
                <StyledTableCell>Element Found</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <StyledTableRow key={row.idtest_step_result || index}>
                  <TableCell className="font-medium">{row.Test_Case_Results_Id}</TableCell>
                  <TableCell>{row.Executed_Date}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.Status}
                      size="small"
                      color={getStatusChipColor(row.Status)}
                      className="font-medium"
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      href={row.Failure_Screenshot_Location}
                      target="_blank"
                      underline="hover"
                      className="text-blue-600 font-medium"
                    >
                      Screenshot
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={row.page_source_file}
                      target="_blank"
                      underline="hover"
                      className="text-blue-600 font-medium"
                    >
                      Page Source
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={row.analysis_log_file}
                      target="_blank"
                      underline="hover"
                      className="text-blue-600 font-medium"
                    >
                      Analysis Log
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      href={row.logFile}
                      target="_blank"
                      underline="hover"
                      className="text-blue-600 font-medium"
                    >
                      Log File
                    </Link>
                  </TableCell>
                  <TableCell className="text-xs max-w-md ">
                    <div className="max-h-48 overflow-y-auto p-2 bg-gray-50 rounded border border-gray-100 flex flex-row">
                      {row.outerhtml}
                      {row.outerhtml && ptcrid && row.Test_Case_Results_Id === ptcrid && (
                        <Button
                          endIcon={<DeleteIcon />}
                          sx={{
                            bgcolor: "red",
                            color: "white",
                            px: 5,
                            maxHeight: "50px",
                          }}
                          onClick={() => {
                            handleDeleteOuterHtml(row.Test_Case_Results_Id, row.idtest_step_result);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <div className="flex justify-center mt-4">
          <Pagination
            count={Math.ceil(tableData.length / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </div>
      </div>
    </div>
  );
};

export default FailureAnalysisPage;
